import { apiRoutes } from '@/constants/api-routes';
import { apiRequest } from './http';
import type {
  AccrualsSummaryView,
  ConnectOnboardingLinkView,
  ConnectStatusView,
  PayoutsListView,
} from '@/types/api';

/**
 * Affiliate-payouts API surface — mirrors `server/src/modules/payouts/`.
 *
 * The onboarding endpoints return single-use Stripe URLs that expire
 * in ~5 minutes; the caller is responsible for redirecting the browser
 * immediately (`window.location.assign`) rather than stashing them.
 */
export const payoutsService = {
  /**
   * Begin (or resume) Stripe Connect Express onboarding. Idempotent
   * server-side — repeat calls reuse the existing connected account.
   */
  startOnboarding() {
    return apiRequest<ConnectOnboardingLinkView>(apiRoutes.payouts.onboarding, {
      method: 'POST',
    });
  },

  /**
   * Mint a "Manage in Stripe" / "Finish setup" link for an affiliate
   * who's already onboarded. Used by the "Manage" affordance once
   * status is `ready`, and by the action-needed CTA.
   */
  getUpdateLink() {
    return apiRequest<ConnectOnboardingLinkView>(apiRoutes.payouts.updateLink, {
      method: 'POST',
    });
  },

  /** Live Connect status (cached 60s server-side). */
  getStatus() {
    return apiRequest<ConnectStatusView>(apiRoutes.payouts.status);
  },

  /** Headline numbers for the dashboard tiles. */
  getSummary() {
    return apiRequest<AccrualsSummaryView>(apiRoutes.payouts.summary);
  },

  /**
   * Paginated payout history. Server clamps `take` to 1..100; default 20.
   */
  listPayouts(opts: { take?: number; skip?: number } = {}) {
    const params = new URLSearchParams();
    if (opts.take !== undefined) params.set('take', String(opts.take));
    if (opts.skip !== undefined) params.set('skip', String(opts.skip));
    const qs = params.toString();
    const path = qs ? `${apiRoutes.payouts.payouts}?${qs}` : apiRoutes.payouts.payouts;
    return apiRequest<PayoutsListView>(path);
  },
};
